import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Grid2, List, ListItem, ListItemIcon, ListItemText, Typography, useTheme, Skeleton } from "@mui/material";
import { Square } from "@mui/icons-material";
import styles from "styles/ProjectList.module.css";
import ClaimsPopup from "./ClaimsPopup";
import { useParams } from "react-router-dom";
import { useGetClaimsOpenAgingQuery } from "store/slices/apis/claimsAndChangeOrderDashboardSlice";


const OpenClaims = () => {
    const chartRef = useRef(null);
    const theme = useTheme();
    const { projectId } = useParams();
    const [popupData, setPopupData] = useState(null);
    const [open, setOpen] = useState(false);
    // const isLoading = false
    const { data, isLoading } = useGetClaimsOpenAgingQuery({ projectId });
    const rawData = data?.data || {};

    const delayRanges = [
        { key: "openSince15Days", label: "Open 0 - 15 Days", color: "#3F766E" },
        { key: "openSince45Days", label: "Open 16 - 45 Days", color: "#FDCA40" },
        { key: "openSince90Days", label: "Open 46 - 90 Days", color: "#EC5540" },
    ];

    const totalOpen = rawData?.totalOpen || 0;

    const analysisData = delayRanges.map(({ key, label, color }) => {
        const count = rawData[key] ?? 0;
        const percentage = totalOpen ? (count / totalOpen) * 100 : 0;
        return { label, color, count, percentage };
    });

    const hasData = analysisData.some(item => item.count > 0);

    const options = {
        chart: {
            type: "pie",
            height: "100%",
            // width: 260,
            // height: 260,
            backgroundColor: 'transparent',
            spacingTop: 10,
            spacingRight: 0,
            spacingBottom: 0,
            spacingLeft: 0,
            events: {
                render() {
                    const chart = this,
                        series = chart.series[0];
                    if (!hasData || totalOpen === 0) {
                        if (chart.options.chart.custom) {
                            chart.options.chart.custom.destroy();
                            chart.options.chart.custom = null;
                        }
                        return;
                    }
                    let customLabel = chart.options.chart.custom;

                    if (!customLabel) {
                        customLabel = chart.options.chart.custom =
                            chart.renderer.label(
                                '<span style="color:#414D55; font-size:28px;font-weight:700">' + totalOpen + '</span><br/>' +
                                '<span style="color:#00000080; font-size:17px;font-weight:400">Total</span>'

                            )
                                .css({
                                    textAnchor: 'middle'
                                })
                                .add();
                    }

                    const x = series.center[0] + chart.plotLeft,
                        y = series.center[1] + chart.plotTop -
                            (customLabel.attr('height') / 2);

                    customLabel.attr({
                        x,
                        y
                    });
                    customLabel.css({
                        fontSize: `${series.center[2] / 12}px`
                    });
                }
            },
        },
        // colors: ["#CE412D", "#FFBF00", "#3E756D"],
        // accessibility: {
        //     point: {
        //         valueSuffix: "%",
        //     },
        // },
        title: {
            text: "",
        },
        subtitle: {
            text: "",
        },
        tooltip: {
            backgroundColor: '#00000080',
            pointFormat: "{point.y}",
            style: {
                color: '#fff',
                fontSize: '14px'
            }
        },
        credits: {
            enabled: false,
        },
        plotOptions: {
            pie: {
                allowPointSelect: false,
                cursor: "pointer",
                size: '100%',
                startAngle: 0,
                innerSize: "75%",
                borderRadius: 0,
                borderWidth: 0,
                backgroundColor: '#fff',
                dataLabels: [
                    {
                        enabled: true,
                        distance: -18,
                        format: "{point.percentage:.1f}%",
                        style: {
                            fontSize: '11px',
                            fontWeight: '600',
                            lineHeight: '100%',
                            letterSpacing: "0.17px",
                            color: '#ffffff',
                            textOutline: 'none',
                            textShadow: 'none'
                        },
                        allowOverlap: true,
                        crop: false,
                        overflow: "justify"
                    },
                ],
                showInLegend: false,
                point: {
                    events: {
                        click: function () {
                            let delayRange = { delayDaysMin: 0, delayDaysMax: 30 };
                            switch (this.name) {
                                case "Open since 15 Days":
                                    delayRange = { delayDaysMin: 0, delayDaysMax: 15 };
                                    break;
                                case "Open since 45 Days":
                                    delayRange = { delayDaysMin: 16, delayDaysMax: 45 };
                                    break;
                                case "Open since 90 Days":
                                    delayRange = { delayDaysMin: 45, delayDaysMax: 1000 };
                                    break;
                            }
                            setPopupData({
                                name: this.name,
                                value: delayRange,
                            });
                            setOpen(true);
                        }
                    }
                }
            },
        },
        series: [
            {
                name: "Registrations",
                innerSize: "60%",
                data: analysisData
                    .filter(item => item.count > 0)
                    .map(item => ({
                        y: item.percentage,
                        color: item.color,
                        name: item.label
                    }))
            },
        ],
    };

    return (
        <Box sx={{ backgroundColor: "white", marginTop: "10px", border: "1px solid #D8D8D8", borderRadius: "12px", pb: "15px" }}>
            <Grid2 container>
                <Grid2 size={{ xs: 12, md: 7 }}>
                    <Box sx={{ paddingLeft: "17px" }}>
                        <Typography variant="h6" sx={{ mb: 1, color: `${theme?.customSettings?.themeColor}`, fontWeight: "500", fontSize: '18px', paddingTop: "20px", lineHeight: "100%", letterSpacing: "0.4px" }}>
                            Open Claims
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: "#182A4E80" }}>
                            Claims that have been submitted but are not yet resolved, closed, or fully settled
                        </Typography>
                    </Box>
                    {isLoading ? (
                        [...Array(3)].map((_, idx) => (
                            <Grid2 container direction="row" spacing={1} key={idx} style={{ marginLeft: "30px", paddingTop: "10px" }}>
                                <Box sx={{ width: "17px", height: "17px", borderRadius: "0", backgroundColor: "#eee", marginTop: "5px" }} />
                                <Skeleton variant="text" width={100} height={24} sx={{ marginLeft: 1 }} />
                                <Skeleton variant="text" width={30} height={28} sx={{ marginLeft: 2 }} />
                            </Grid2>
                        ))
                    ) : (
                        hasData ? (
                            analysisData.map((item, idx) => (
                                <Grid2 container direction="row" spacing={1} key={idx} style={{ marginLeft: "10px", paddingTop: idx === 0 ? "10px" : "0px" }}>
                                    <Typography style={{ padding: "8px", width: "25px" }}>
                                        <Square style={{ color: item?.color, height: '17px', width: '17px' }} />
                                    </Typography>
                                    <Typography style={{
                                        padding: "9px", width: "100px", color: "#182A4E", fontSize: "16px", fontWeight: "500",
                                        lineHeight: "100%", whiteSpace: "nowrap", letterSpacing: "-0.4px"
                                    }}>
                                        {item?.label}: {item?.count}
                                    </Typography>
                                </Grid2>
                            ))
                        ) : (
                            <Typography sx={{ color: '#999', fontSize: 16, marginLeft: 4, marginTop: 6, whiteSpace: "nowrap" }}>
                                No data available to display the chart.
                            </Typography>
                        )
                    )}

                </Grid2>
                <Grid2 size={{ xs: 12, md: 5 }}>
                    <Typography style={{ marginTop: "60px" }}>
                        {isLoading ? (
                            <Skeleton variant="circular" width={150} height={150} sx={{ marginTop: "75px" }} />
                        ) : (
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={options}
                                ref={chartRef}
                            />
                        )}
                    </Typography>
                </Grid2>
            </Grid2>
            {open &&
                <ClaimsPopup title={`Claims ${popupData?.name}`} value={popupData?.value} status="openClaims" onOpen={open} onClose={() => setOpen(false)} />
            }
        </Box>
    );
};

export default OpenClaims;
